
import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Connection, Organization, Provider } from "../common/ObjectTypes";
import SelectInput from "../common/SelectInput";
import { AttentionIcon } from "../common/ProjectIcons";
import { buttonClasses, inputClasses } from "../common/tailwindClassnames";

type ConnectionOverviewProps = {
    connection: Connection,
    errors: any,
    connectionProvider: Provider,
    onChangeConnectionName: Function,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function,
    onChangeConnectionOwner: Function,
    onUpdateConnection: MouseEventHandler,
    onCancelConnectionChanges: MouseEventHandler,
    changesMade: boolean,
}

const ConnectionDetailsOverview = ({
    connection,
    errors,
    connectionProvider,
    onChangeConnectionName,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    onChangeConnectionOwner,
    onUpdateConnection,
    onCancelConnectionChanges,
    changesMade,
}: ConnectionOverviewProps
) => {
    const { t } = useTranslation();

    return (
        <div className="mt-8">
            {/* div with configuration details */}
            <div className="flex flex-col w-full p-4">
                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                    {/* Connection Name row */}
                    <div className="mb-[8px] border-b border-divider-lines pt-1 pb-[8px] transition-all duration-300 w-full">
                        <div className="flex flex-row items-start justify-between w-full gap-6">
                            <div className="w-1/2 flex flex-col">
                                <label htmlFor="name" className="mr-1">{t("Connection Name")} *</label>
                                <span className="text-sm text-secondary-gray mt-1">{t("A descriptive name to identify this connection.")}</span>
                            </div>
                            <div className="w-1/2">
                                <div className="p-2">
                                    <input
                                        id="name"
                                        placeholder=""
                                        value={connectionProvider.name}
                                        className={`${inputClasses.standard} w-full`}
                                        onChange={(e) => onChangeConnectionName(e)}
                                        disabled={false}
                                    />
                                </div>
                                {errors.provider_name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.provider_name}</p></div>}
                            </div>
                        </div>
                    </div>
                    {/* Workspace row */}
                    <div className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0 transition-all duration-300 w-full">
                        <div className="flex flex-row items-start justify-between w-full gap-6">
                            <div className="w-1/2 flex flex-col">
                                <label htmlFor="workspace" className="mr-1">{t("Workspace")} *</label>
                                <span className="text-sm text-secondary-gray mt-1">{t("The workspace this connection belongs to.")}</span>
                            </div>
                            <div className="w-1/2">
                                <div className="p-2">
                                    <SelectInput
                                        inputId="workspace"
                                        options={organizations.map((org: Organization) => {
                                            return {
                                                label: org.name,
                                                value: org.id
                                            }
                                        })}
                                        className={"basic-single"}
                                        value={{ label: connectionProvider?.owner?.name ?? connection?.owner?.name ?? '', value: connectionProvider?.owner?.id ?? connection?.owner?.id ?? '' }}
                                        autoSelectSingleOption={true}
                                        isDisabled={false}
                                        isLoading={false}
                                        isClearable={false}
                                        isSearchable={true}
                                        isMulti={false}
                                        name={t("Workspace")}
                                        onChange={(e: any) => onChangeConnectionOwner(e)}
                                        onMenuScrollToBottom={loadMoreOrgs}
                                        handleInputChange={orgInputChange}
                                    />
                                </div>
                                {errors.provider_owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.provider_owner}</p></div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* buttons */}
            <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 w-full flex flex-row bg-white/10 backdrop-blur transition-all duration-300">
                <button
                    className={changesMade ? `${buttonClasses.primary} mr-3` : `${buttonClasses.disabled} mr-3`}
                    disabled={!changesMade}
                    onClick={onUpdateConnection}>
                    {t("Save")}
                </button>
                <button
                    className={`${buttonClasses.textButton} px-3 mx-3`}
                    onClick={onCancelConnectionChanges}
                >
                    {t("Cancel")}
                </button>
            </div>
        </div>
    )
};

export default ConnectionDetailsOverview;